import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../supabase/config';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [tenantData, setTenantData] = useState(null);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState(null);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const isSigningUp = useRef(false);

    const effectiveTenantId = selectedTenantId || userData?.tenantId;

    const signup = async (email, password, corporateData) => {
        isSigningUp.current = true;
        try {
            const { userCredential, userDoc } = await authService.signup(email, password, corporateData);
            setUserData(userDoc);
            return userCredential;
        } finally {
            isSigningUp.current = false;
        }
    };

    const login = (email, password) => authService.login(email, password);
    const superAdminLogin = (email, password, token) => authService.superAdminLogin(email, password, token);

    const logout = async () => {
        return await authService.logout();
    };

    const switchTenant = (tenantId) => setSelectedTenantId(tenantId);
    const resetTenant = () => setSelectedTenantId(null);

    const isSuperAdmin = userData?.isSuperAdmin || false;

    // Augment userData so existing pages checking userData.isSuperAdmin will pass without changes
    const augmentedUserData = userData ? { ...userData, isSuperAdmin } : null;

    const hasPermission = (module, action) => {
        if (userData?.role === 'owner' || isSuperAdmin) return true;
        if (!permissions) return false;
        const modulePerms = permissions[module];
        return modulePerms ? modulePerms[action] === true : false;
    };

    // ──────────────────────────────────────────────
    // 0. Failsafe — ensures loading=false after 5s if app is stuck
    // ──────────────────────────────────────────────
    useEffect(() => {
        if (!loading) return;
        
        const failsafeTimeout = setTimeout(() => {
            setLoading(prev => {
                if (prev) {
                    console.warn('[Auth] Failsafe: forcing loading=false after 5s');
                    return false;
                }
                return prev;
            });
        }, 5000);
        
        return () => clearTimeout(failsafeTimeout);
    }, [loading]);

    // ──────────────────────────────────────────────
    // 1. Auth Listener — ONLY sets user, never queries DB
    // ──────────────────────────────────────────────
    useEffect(() => {
        // Read cached session synchronously (no network call)
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user || null);
            if (!session?.user) {
                setLoading(false);
            }
            // If user exists, the useEffect below will load userData
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                console.log('[Auth] onAuthStateChange:', event);

                // Synchronous state updates only — no awaits, no DB queries
                const currentUser = session?.user || null;
                setUser(currentUser);

                if (isSigningUp.current) return;

                if (event === 'SIGNED_OUT') {
                    setUserData(null);
                    setTenantData(null);
                    setPermissions(null);
                    setSelectedTenantId(null);
                    setLoading(false);
                }
            }
        );

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    // ──────────────────────────────────────────────
    // 2. Load userData when user changes — runs AFTER auth init
    // ──────────────────────────────────────────────
    useEffect(() => {
        if (!user) {
            // No user — clear everything
            if (userData) {
                setUserData(null);
                setTenantData(null);
                setPermissions(null);
                setSelectedTenantId(null);
            }
            setLoading(false);
            return;
        }

        // Skip if signup is in progress (signup sets userData directly)
        if (isSigningUp.current) return;

        // Skip if we already have userData for this user
        if (userData && userData.email === user.email) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const fetchUserData = async () => {
            try {
                console.log('[Auth] Fetching userData for:', user.id);
                let data = await authService.getUserData(user.id);

                if (cancelled) return;

                if (!data) {
                    console.warn('[Auth] No user record in users table for uid:', user.id);
                }

                if (user.email === 'admin@crmapp.com') {
                    data = {
                        ...(data || {}),
                        email: user.email,
                        isSuperAdmin: true,
                        role: 'admin',
                        firstName: data?.firstName || 'System',
                        lastName: data?.lastName || 'Administrator'
                    };
                }

                if (!cancelled) {
                    setUserData(data);
                    setLoading(false);
                }
            } catch (err) {
                console.error('[Auth] fetchUserData failed:', err.message);
                if (!cancelled) setLoading(false);
            }
        };

        fetchUserData();

        return () => { cancelled = true; };
    }, [user?.id]);

    // ──────────────────────────────────────────────
    // 3. Tenant Sync — fetches tenant when effectiveTenantId changes
    // ──────────────────────────────────────────────
    useEffect(() => {
        let channel = null;

        if (effectiveTenantId) {
            const fetchTenant = async () => {
                try {
                    // Try Redis-cached API first (with 3s timeout) - Skip on localhost to avoid persistent 404 console noise
                    let data = null;
                    if (window.location.hostname !== 'localhost') {
                        try {
                            const controller = new AbortController();
                            const timeout = setTimeout(() => controller.abort(), 3000);
                            const resp = await fetch(`/api/tenant?id=${encodeURIComponent(effectiveTenantId)}`, { signal: controller.signal });
                            clearTimeout(timeout);
                            if (resp.ok) {
                                data = await resp.json();
                            }
                        } catch (err) {
                            // Silent failure for cache, fallback triggered below
                        }
                    }

                    // Fallback to direct Supabase
                    if (!data) {
                        const cleanId = effectiveTenantId.replace(/^tenant_/, '');

                        // Try 1: Original ID as-is (handles prefix'li IDs in DB)
                        let { data: sbData } = await supabase
                            .from('tenants')
                            .select('*')
                            .eq('id', effectiveTenantId)
                            .maybeSingle();

                        // Try 2: Cleaned ID without prefix
                        if (!sbData && cleanId !== effectiveTenantId) {
                            const res = await supabase
                                .from('tenants')
                                .select('*')
                                .eq('id', cleanId)
                                .maybeSingle();
                            sbData = res.data;
                        }

                        // Try 3: Slug fallback
                        if (!sbData) {
                            const res = await supabase
                                .from('tenants')
                                .select('*')
                                .eq('slug', effectiveTenantId)
                                .maybeSingle();
                            sbData = res.data;
                        }

                        data = sbData;
                    }

                    if (data) {
                        // Default plan in-memory only — do NOT write 'free' back to DB
                        // (it might already be 'pro' and we just have a data shape issue)
                        if (!data.plan) {
                            console.warn('[Auth] Tenant has no plan set, defaulting to free in-memory');
                            data.plan = 'free';
                        }
                        setTenantData({ id: data.id, ...data });
                    }
                } catch (err) {
                    console.error("Tenant Fetch Error:", err);
                }
            };

            fetchTenant();

            // Real-time subscription for tenant changes
            // Use effectiveTenantId directly — the DB row ID might include the tenant_ prefix
            channel = supabase
                .channel(`tenant-${effectiveTenantId}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'tenants',
                        filter: `id=eq.${effectiveTenantId}`
                    },
                    (payload) => {
                        const record = payload.new;
                        if (record) {
                            if (!record.plan) record.plan = 'free'; // In-memory only
                            setTenantData({ id: record.id, ...record });
                        }
                    }
                )
                .subscribe();
        } else {
            setTenantData(null);
        }

        return () => {
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [effectiveTenantId]);

    // ──────────────────────────────────────────────
    // 4. Permission Sync
    // ──────────────────────────────────────────────
    useEffect(() => {
        let channel = null;

        if (userData?.role && userData.role !== 'owner' && effectiveTenantId) {
            setPermissionsLoading(true);

            const fetchPermissions = async () => {
                try {
                    const { data, error } = await supabase
                        .from('roles')
                        .select('permissions')
                        .eq('tenant_id', effectiveTenantId)
                        .eq('name', userData.role)
                        .limit(1)
                        .single();

                    if (error && error.code !== 'PGRST116') {
                        console.error("Permission Sync Error:", error);
                        setPermissions({});
                    } else {
                        setPermissions(data?.permissions || {});
                    }
                } catch (err) {
                    console.error("Permission Sync Error:", err);
                    setPermissions({});
                }
                setPermissionsLoading(false);
            };

            fetchPermissions();

            channel = supabase
                .channel(`roles-${effectiveTenantId}-${userData.role}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'roles',
                        filter: `tenant_id=eq.${effectiveTenantId}`
                    },
                    (payload) => {
                        const record = payload.new;
                        if (record && record.name === userData.role) {
                            setPermissions(record.permissions || {});
                        }
                    }
                )
                .subscribe();
        } else {
            setPermissions(null);
            setPermissionsLoading(false);
        }

        return () => {
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [userData?.role, effectiveTenantId]);

    // ──────────────────────────────────────────────
    // 5. Periodic health check — refresh token before expiry
    // ──────────────────────────────────────────────
    useEffect(() => {
        if (!user) return;

        const healthCheck = setInterval(async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) return;

                const expiresAt = session.expires_at;
                const now = Math.floor(Date.now() / 1000);
                if (expiresAt && expiresAt - now < 120) {
                    console.log('[Auth] Health check: refreshing token...');
                    await supabase.auth.refreshSession();
                }
            } catch (err) {
                console.error('[Auth] Health check failed:', err);
            }
        }, 60000);

        return () => clearInterval(healthCheck);
    }, [user?.id]);

    const value = {
        user, userData: augmentedUserData, tenantData, permissions, permissionsLoading,
        hasPermission, selectedTenantId, effectiveTenantId, isSuperAdmin,
        switchTenant, resetTenant, signup, login, superAdminLogin, logout, loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
